import { useState } from 'react';
import { stopShowing } from '@/app/common/reducers/helpersShowSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useGetNotifsQuery } from '@/app/common/api/notifApi';
import MarkdownRenderer from '@/app/PageSpeed/components/audit-renderers/MarkdownRenderer';
import { Modal } from '@/components/common/AppModal';
import { Alert, Button, CloseButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataLoadError from '@/components/errors/DataLoadError';

type HelpersShowState = string[];

const NoticeTop = () => {
  const [show, setShow] = useState(true);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const dispatch = useDispatch();
  const helpersShowState = useSelector(
    (state: { helpersShow: HelpersShowState }) => state.helpersShow,
  );
  const { data, isLoading, isError, isFetching, refetch } = useGetNotifsQuery({
    filter: { type: 'top', is_active: true },
    sort: '-createdAt',
  });
  const notif = data?.data?.[0];
  const hasAttachment = !!notif?.attachment;
  const isVideo = !!notif?.attachment_is_video;
  const isExpired = (() => {
    if (!notif?.expiration_date) return false;
    const exp = new Date(notif.expiration_date);
    if (Number.isNaN(exp.getTime())) return false;
    const now = new Date();
    return exp < now;
  })();

  const handleCloseTop = () => {
    setShow(false);
    if (notif?.id_display) {
      dispatch(stopShowing(`noticeTop:${notif?.id}`));
    }
  };

  if (isLoading) return <></>;

  if (isError) {
    return (
      <DataLoadError
        className="mt-2"
        onRetry={() => refetch()}
        isRetrying={isFetching}
      />
    );
  }

  return (
    <>
      {show &&
        notif &&
        !isExpired &&
        !helpersShowState?.includes(`noticeTop:${notif?.id}`) && (
          <Alert
            variant="warning"
            className="mt-2 d-flex align-items-center fs--1"
          >
            <div
              className="bg-warning d-flex flex-center me-3 shadow-sm border-0 overflow-hidden position-relative"
              style={{
                height: hasAttachment ? '2.5rem' : '2.0rem',
                width: hasAttachment ? '4.5rem' : '2.0rem',
                borderRadius: hasAttachment ? '0.4rem' : '3rem',
                cursor: hasAttachment ? 'pointer' : 'default',
              }}
              onClick={
                hasAttachment ? () => setShowAttachmentModal(true) : undefined
              }
              role={hasAttachment ? 'button' : undefined}
            >
              {hasAttachment && isVideo ? (
                <>
                  <video
                    muted
                    playsInline
                    className="w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  >
                    <source src={notif?.attachment} />
                  </video>
                  <FontAwesomeIcon
                    icon="play-circle"
                    className="text-white"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '1.3rem',
                      textShadow: '0 0 6px rgba(0,0,0,0.6)',
                    }}
                  />
                </>
              ) : hasAttachment && !isVideo ? (
                <img
                  src={notif?.attachment}
                  alt={notif?.title_dispaly || ''}
                  className="w-100 h-100  bg-100"
                  style={{ objectFit: 'cover' }}
                />
              ) : (
                <FontAwesomeIcon
                  icon="exclamation-circle"
                  className="fs-2 text-white"
                />
              )}
            </div>
            <div className="flex-1">
              <div className="d-flex align-items-center justify-content-between w-100">
                <span className="flex-1 me-2">
                  {notif?.content && (
                    <MarkdownRenderer
                      content={notif?.content}
                      openLinksInNewTab
                    />
                  )}
                </span>
                <div className="d-flex align-items-center gap-2">
                  {notif?.btn_url && notif?.btn_text && (
                    <Button
                      size="sm"
                      className="mx-1"
                      variant="falcon-primary"
                      href={notif?.btn_url}
                      target="_blank"
                    >
                      {notif?.btn_text}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <CloseButton onClick={handleCloseTop} className="ms-2" />
          </Alert>
        )}

      {notif && hasAttachment && !isExpired && (
        <Modal
          show={showAttachmentModal}
          onHide={() => setShowAttachmentModal(false)}
          centered
          size="lg"
          contentClassName="notif-modal-content"
        >
          <Modal.Header closeButton className="align-items-start gap-2">
            <Modal.Title className="fs-0 text-break">
              {notif?.title_dispaly || 'اطلاعیه'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="fs--1 p-0">
            {isVideo ? (
              <video
                controls
                playsInline
                className="w-100"
                style={{ maxHeight: '50vh', objectFit: 'contain' }}
              >
                <source src={notif?.attachment} />
              </video>
            ) : (
              <img
                src={notif?.attachment}
                alt={notif?.title_dispaly || ''}
                className="img-fluid w-100"
              />
            )}
            <div className="px-3 py-3 text-break">
              <MarkdownRenderer
                content={notif?.content}
                openLinksInNewTab
              />
            </div>
          </Modal.Body>
          {notif?.btn_text && (
            <Modal.Footer className="flex-wrap">
              <Button
                as="a"
                href={notif?.btn_url}
                variant="secondary"
                className="mw-100 text-truncate"
                target="_blank"
              >
                {notif?.btn_text}
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </>
  );
};

export default NoticeTop;
